import React, { useState } from "react";
import { decimalsFor } from "../utils/instrument";
import { useApi } from "../hooks/useApi";
import { format } from "date-fns";

const card = { background: "#0d1224", border: "1px solid #1e2a45", borderRadius: "10px", padding: "16px" };
const tbl  = { width: "100%", borderCollapse: "collapse", fontSize: "11px" };
const th   = { padding: "5px 8px", textAlign: "left", color: "#475569", borderBottom: "1px solid #1e2a45", fontWeight: 500, whiteSpace: "nowrap" };
const td   = { padding: "5px 8px", borderBottom: "1px solid #0f172a", whiteSpace: "nowrap" };

// v5.16: the filter list is derived from the trades actually returned, so it can
// never offer an asset the engine no longer trades — nor omit a new one. The old
// hardcoded list still showed USDJPY/USDCHF/USDCAD after the v5.14 swap.
function symbolOptions(rows) {
  const seen = Array.from(new Set((rows || []).map(r => r.symbol).filter(Boolean)));
  return ["", ...seen.sort()];
}

// v5.16: decimals come from the instrument. See utils/instrument.js.
function dp(sym, price) {
  return decimalsFor(sym, price);
}

function fmtDur(mins) {
  if (mins == null) return "—";
  if (mins === 0) return "<1m";
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h${m}m` : `${h}h`;
}

export default function TradeHistory({ limit = 20 }) {
  const [symbol, setSymbol] = useState("");
  const url = `/trade_history?limit=${limit}${symbol ? `&symbol=${symbol}` : ""}`;
  const { data, loading } = useApi(url, 30000);
  const trades = data?.trades || [];
  const summary = data?.summary;

  // Local page stats (labelled as such — system stats come from server summary)
  const closed = trades.filter((t) => ["WIN","LOSS","CLOSED","PARTIAL"].includes(t.result));
  const pagePnl = closed.reduce((s, t) => s + (t.profit_usd || 0), 0);

  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", flexWrap: "wrap", gap: "8px" }}>
        <span style={{ fontWeight: 600, color: "#94a3b8" }}>
          Trade History
          <select value={symbol} onChange={(e) => setSymbol(e.target.value)}
            style={{ marginLeft: "10px", background: "#0f172a", color: "#94a3b8", border: "1px solid #1e2a45",
                     borderRadius: "4px", fontSize: "11px", padding: "2px 6px" }}>
            {symbolOptions(trades).map((s) => <option key={s} value={s}>{s || "All assets"}</option>)}
          </select>
        </span>
        <div style={{ fontSize: "11px", color: "#64748b", display: "flex", gap: "12px", flexWrap: "wrap" }}>
          {summary?.win_rate != null && (
            <span>All-time WR: <b style={{ color: summary.win_rate >= 55 ? "#4ade80" : "#60a5fa" }}>{summary.win_rate}%</b> ({summary.wins}/{summary.closed_total})</span>
          )}
          {summary?.net_pnl != null && (
            <span>Net: <b style={{ color: summary.net_pnl >= 0 ? "#4ade80" : "#f87171" }}>${summary.net_pnl.toFixed(2)}</b></span>
          )}
          {summary?.avg_rr != null && <span>Avg R: <b style={{ color: "#60a5fa" }}>{summary.avg_rr}</b></span>}
          <span style={{ color: "#475569" }}>last {closed.length}: ${pagePnl.toFixed(2)}</span>
        </div>
      </div>

      {loading && <div style={{ color: "#475569", fontSize: "12px", padding: "20px 0" }}>Loading history...</div>}

      {!loading && trades.length === 0 && (
        <div style={{ color: "#334155", textAlign: "center", padding: "30px 0", fontSize: "12px" }}>
          No closed trades yet
        </div>
      )}

      {trades.length > 0 && (
        <div style={{ overflowX: "auto", maxHeight: "400px", overflowY: "auto" }}>
          <table style={tbl}>
            <thead>
              <tr>
                {["Open","Closed","Symbol","Side","Lots","Entry","Close","Pips","P&L","R:R","Risk $","Conf","Peak","Slip","Sess","Dur","Exit","Tr"].map((h) => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {trades.map((t) => {
                const d = dp(t.symbol, t.close_price ?? t.price);
                const pnlColor = (t.profit_usd || 0) > 0 ? "#4ade80" : (t.profit_usd || 0) < 0 ? "#f87171" : "#94a3b8";
                const pipsVal = t.profit_pips;
                const pipsStr = pipsVal != null ? (pipsVal >= 0 ? `+${pipsVal.toFixed(1)}` : pipsVal.toFixed(1)) : "—";
                const giveback = t.max_profit > 0 && t.profit_usd != null
                  ? Math.max(0, 100 - (t.profit_usd / t.max_profit) * 100) : null;
                return (
                  <tr key={t.id}>
                    <td style={td}>{t.timestamp ? format(new Date(t.timestamp), "MM/dd HH:mm") : "—"}</td>
                    <td style={td}>{t.closed_at ? format(new Date(t.closed_at), "MM/dd HH:mm") : "—"}</td>
                    <td style={td}><b style={{ color: "#e2e8f0" }}>{t.symbol}</b></td>
                    <td style={td}><span style={{ color: t.action === "BUY" ? "#4ade80" : "#f87171" }}>{t.action}</span></td>
                    <td style={td}>{t.lots != null ? t.lots.toFixed(2) : "—"}</td>
                    <td style={td}>{t.price?.toFixed(d) || "—"}</td>
                    <td style={td}>{t.close_price?.toFixed(d) || "—"}</td>
                    <td style={{ ...td, color: pipsVal > 0 ? "#4ade80" : pipsVal < 0 ? "#f87171" : "#94a3b8" }}>{pipsStr}</td>
                    <td style={{ ...td, color: pnlColor, fontWeight: 600 }}>
                      {t.profit_usd != null ? `${t.profit_usd >= 0 ? "+" : ""}$${t.profit_usd.toFixed(2)}` : "—"}
                    </td>
                    <td style={td}>{t.rr_achieved?.toFixed(2) || "—"}</td>
                    <td style={td}>{(t.initial_risk ?? t.risk_usd) != null ? `$${(t.initial_risk ?? t.risk_usd).toFixed(0)}` : "—"}</td>
                    <td style={{ ...td, color: t.ai_confidence >= 0.65 ? "#4ade80" : "#94a3b8" }}>
                      {t.ai_confidence != null && t.ai_confidence > 0 ? `${(t.ai_confidence * 100).toFixed(0)}%` : "—"}
                    </td>
                    <td style={{ ...td, color: giveback > 60 ? "#fbbf24" : "#94a3b8" }}
                        title={giveback != null ? `gave back ${giveback.toFixed(0)}% of peak` : ""}>
                      {t.max_profit != null && t.max_profit > 0 ? `$${t.max_profit.toFixed(0)}` : "—"}
                    </td>
                    <td style={td}>{t.slippage_pips != null ? t.slippage_pips.toFixed(1) : "—"}</td>
                    <td style={{ ...td, fontSize: "10px", color: "#64748b" }}>{t.session || "—"}</td>
                    <td style={td}>{fmtDur(t.duration_mins)}</td>
                    <td style={{ ...td, color: "#64748b", fontSize: "10px" }}>{t.exit_reason || t.result || "—"}</td>
                    <td style={td}>{t.tranche_id || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
